/**
 * PKCE (Proof Key for Code Exchange) utilities for OAuth 2.0
 *
 * PKCE adds security to the OAuth authorization code flow by:
 * 1. Generating a random code_verifier
 * 2. Creating a code_challenge = base64url(SHA256(code_verifier))
 * 3. Sending code_challenge with the authorize request
 * 4. Sending code_verifier with the token exchange request
 *
 * This prevents authorization code interception attacks.
 */

const PKCE_STORAGE_KEY = 'pkce_code_verifier';

/**
 * Generate a cryptographically random code_verifier
 * Must be 43-128 characters, using unreserved URI characters
 */
export const generateCodeVerifier = (): string => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64UrlEncode(array);
};

/**
 * Generate code_challenge from code_verifier using SHA-256
 */
export const generateCodeChallenge = async (codeVerifier: string): Promise<string> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(digest));
};

/**
 * Base64 URL encode (RFC 4648)
 * Standard base64 with + → -, / → _, and no padding
 */
const base64UrlEncode = (buffer: Uint8Array): string => {
  const base64 = btoa(String.fromCharCode(...buffer));
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

/**
 * Store code_verifier in sessionStorage for later retrieval
 * Uses a key that includes the state parameter to support multiple concurrent flows
 */
export const storeCodeVerifier = (state: string, codeVerifier: string): void => {
  const key = `${PKCE_STORAGE_KEY}_${state}`;
  sessionStorage.setItem(key, codeVerifier);
};

/**
 * Retrieve and remove code_verifier from sessionStorage
 */
export const retrieveCodeVerifier = (state: string): string | null => {
  const key = `${PKCE_STORAGE_KEY}_${state}`;
  const verifier = sessionStorage.getItem(key);
  if (verifier) {
    sessionStorage.removeItem(key);
  }
  return verifier;
};

/**
 * Generate PKCE parameters for OAuth flow
 * Returns code_verifier (to store) and code_challenge (to send)
 */
export const generatePkceParams = async (): Promise<{
  codeVerifier: string;
  codeChallenge: string;
}> => {
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);
  return { codeVerifier, codeChallenge };
};
